import { NamedProject } from './project-deep-link';

/**
 * The canonical project URL: /project/{serial}/{name}/{tab}
 *
 * Ruled by Joel, 2026-09-04. URL IS STATE, so a reload, a bookmark and an
 * auth-recovery return all restore the same screen without the app having to
 * remember anything.
 *
 * WHY THE SERIAL IS IN THE PATH, and this is the part worth reading:
 *
 * PROJECT NAMES ARE NOT UNIQUE. They are generated per device — "project3",
 * "Unnamed_2026-08-17_09-41-58" — and the server queries them per CMS
 * (SELECT * FROM c WHERE c.SER = @serial), so two CMS units under one customer can
 * hold projects with the SAME name. Nothing has ever prevented it.
 *
 * That makes the existing ?project=<name> deep link AMBIGUOUS BY CONSTRUCTION, and
 * findProjectByName resolves it by returning the FIRST exact match in the list. So
 * today, where two same-named projects exist, a shared link opens whichever the
 * server happened to list first — silently, and with no way for either party to
 * tell. It is a pre-existing defect, independent of this work, and the serial in
 * the path is what fixes it: (serial, name) IS unique.
 */

/** Root segment of the canonical project URL. */
export const PROJECT_ROUTE_ROOT = 'project';

/** The tab shown when a link names a project but no tab. */
export const DEFAULT_PROJECT_TAB = 'temperature';

/**
 * The tabs that render a cure, and therefore the tabs that can appear in a project
 * URL. Same list the gate disables without a project — one definition, because a
 * tab that is gated but not routable (or the reverse) is a tab that half-works.
 */
export const PROJECT_TABS: readonly string[] = [
  'temperature', 'graph', 'curing', 'health', 'notes', 'subviews', 'info',
];

/**
 * Is the user on a project tab AT ALL?
 *
 * STRICT, and deliberately not the same question as currentTabFromUrl. That one
 * answers "which tab should I navigate to" and FALLS BACK to the default, so it
 * returns 'temperature' for /admin/devices and cannot be used to ask "am I on a
 * project view?" — a caller that tried would conclude yes, everywhere.
 *
 * Exists because MainLayoutComponent renders chrome AFTER its router-outlet, so
 * anything not gated on this paints underneath every routed screen — the selection
 * grid and the admin console included.
 */
export function isProjectTabUrl(url: string | null | undefined): boolean {
  const canonical = parseProjectRoute(url);
  if (canonical) return PROJECT_TABS.includes(canonical.tab);

  const first = ((url ?? '').split('?')[0].split('#')[0].split('/')[1] ?? '').toLowerCase();
  return PROJECT_TABS.includes(first);
}

/**
 * Which project tab the user is on, from the URL.
 *
 * FROM THE URL, NOT FROM COMPONENT STATE, because on a cold load nothing has told
 * any component which tab it is rendering yet — and that is exactly the case this
 * has to be right for. Understands both the canonical
 * /project/{serial}/{name}/{tab} shape and the flat /{tab} routes that precede it,
 * so selection keeps the user where they are under either.
 *
 * Falls back to the default for anything that is not a project tab: selecting a
 * project from an admin screen goes to the default rather than staying on admin,
 * because admin is not a project view and staying would show a selection that
 * renders nothing.
 */
export function currentTabFromUrl(url: string | null | undefined): string {
  const canonical = parseProjectRoute(url);
  if (canonical && PROJECT_TABS.includes(canonical.tab)) return canonical.tab;

  const first = ((url ?? '').split('?')[0].split('#')[0].split('/')[1] ?? '').toLowerCase();
  return PROJECT_TABS.includes(first) ? first : DEFAULT_PROJECT_TAB;
}

export interface ProjectRoute {
  serial: string;
  name: string;
  tab: string;
}

/** A project carrying the two things that identify it. */
export interface IdentifiableProject extends NamedProject {
  SerialNumber?: string | null;
}

/**
 * Build the canonical URL for a project.
 *
 * Every segment is percent-encoded: real project names contain spaces, '&', '#'
 * and '+', and a raw name would truncate the path or, worse, silently change which
 * project it names.
 */
export function buildProjectRoute(
  serial: string | null | undefined,
  name: string | null | undefined,
  tab: string = DEFAULT_PROJECT_TAB
): string {
  const segments = [
    PROJECT_ROUTE_ROOT,
    encodeURIComponent((serial ?? '').trim()),
    encodeURIComponent((name ?? '').trim()),
    encodeURIComponent((tab || DEFAULT_PROJECT_TAB).trim()),
  ];
  return `/${segments.join('/')}`;
}

/**
 * Parse a canonical URL back into its parts, or null when it is not one.
 *
 * Returns null rather than a partially-filled object: a URL missing the serial is
 * not "a project route with an unknown serial", it is a different URL, and treating
 * it as the former is how an ambiguous lookup creeps back in.
 */
export function parseProjectRoute(url: string | null | undefined): ProjectRoute | null {
  const path = (url ?? '').split('?')[0].split('#')[0];
  const parts = path.split('/').filter(Boolean);

  if (parts.length < 3 || parts[0] !== PROJECT_ROUTE_ROOT) return null;

  const decode = (segment: string): string | null => {
    try {
      return decodeURIComponent(segment);
    } catch {
      // Malformed percent-encoding. Absent beats guessing.
      return null;
    }
  };

  const serial = decode(parts[1]);
  const name = decode(parts[2]);
  const tab = parts.length > 3 ? decode(parts[3]) : DEFAULT_PROJECT_TAB;

  if (!serial || !name || !tab) return null;
  return { serial, name, tab };
}

/** Find the one project a canonical route names. Unambiguous: (serial, name) is unique. */
export function findProjectByRoute<T extends IdentifiableProject>(
  projects: readonly T[] | null | undefined,
  route: ProjectRoute | null
): T | null {
  if (!route || !projects) return null;
  for (const project of projects) {
    if (
      project?.ProjectName === route.name &&
      (project?.SerialNumber ?? '').toUpperCase() === route.serial.toUpperCase()
    ) {
      return project;
    }
  }
  return null;
}

/**
 * What a legacy ?project=<name> link resolves to.
 *
 * The alias is PERMANENT, by ruling: links are already in the wild and breaking
 * them costs real people real time for no benefit. It redirects to the canonical
 * path once the list resolves, so a shared link upgrades itself the first time it
 * is opened.
 *
 * 'ambiguous' is a REAL OUTCOME, not a defensive branch. It is precisely the
 * pre-existing defect: more than one project can carry the name, and the old
 * findProjectByName silently took the first. A caller must ask rather than guess —
 * the selection screen, filtered to the candidates, is a correct answer where
 * opening the wrong cure is not.
 */
export type AliasResolution =
  | { status: 'none' }
  | { status: 'resolved'; url: string; project: IdentifiableProject }
  | { status: 'ambiguous'; name: string; candidates: IdentifiableProject[] }
  | { status: 'not-found'; name: string };

export function resolveLegacyProjectAlias<T extends IdentifiableProject>(
  name: string | null | undefined,
  projects: readonly T[] | null | undefined,
  tab: string = DEFAULT_PROJECT_TAB
): AliasResolution {
  const wanted = (name ?? '').trim();
  if (!wanted) return { status: 'none' };

  // Exact match only, as the deep link has always done: project names are
  // user-supplied and near-duplicates are common, so a loose match could open the
  // wrong cure. A miss shows the picker; a wrong hit shows wrong data.
  const candidates = (projects ?? []).filter((p) => p?.ProjectName === wanted);

  if (candidates.length === 0) return { status: 'not-found', name: wanted };
  if (candidates.length > 1) return { status: 'ambiguous', name: wanted, candidates: [...candidates] };

  const project = candidates[0];
  return {
    status: 'resolved',
    url: buildProjectRoute(project.SerialNumber, project.ProjectName, tab),
    project,
  };
}
